// Auto-focused barcode input. On Enter:
//   - If a serialResolver is provided: tries /v1/scan first (serial-first for IMEI).
//     kind=serial → onSerialAdd callback; kind=variant → falls through to onAdd/onMultiple.
//   - Otherwise (no resolver): falls back to /v1/variants/search as before.
import { useRef, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { apiGet } from "@/lib/api/client";
import type { ScanResult } from "@/features/inventory/use-scan-resolve";

interface VariantResult {
  id: string;
  sku: string;
  barcode?: string | null;
  productId: string;
  productName: string;
  sellPrice: string;
  costPrice: string;
  imageUrls?: string[];
}

interface Props {
  onAdd: (variantId: string, imageUrl?: string) => void;
  onMultiple: (results: VariantResult[]) => void;
  disabled?: boolean;
  /**
   * When provided, the barcode input runs serial-first scan via this resolver
   * before falling back to variant search. The callback is called when a
   * kind=serial & ok=true result is obtained.
   */
  serialResolver?: (q: string) => Promise<ScanResult>;
  onSerialAdd?: (variantId: string, serialId: string) => void;
  onSerialError?: (reason: string) => void;
}

export function BarcodeInput({
  onAdd,
  onMultiple,
  disabled,
  serialResolver,
  onSerialAdd,
  onSerialError,
}: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  // Restore focus when component mounts or after each action.
  useEffect(() => {
    const id = setTimeout(() => ref.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [disabled]);

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || !value.trim() || busy) return;
    e.preventDefault();
    const q = value.trim();
    setBusy(true);
    try {
      // Serial-first path: only when a resolver and its callback are wired in.
      if (serialResolver && onSerialAdd) {
        const sr = await serialResolver(q);
        if (sr.kind === "serial") {
          setValue("");
          if (sr.ok) {
            onSerialAdd(sr.variant.id, sr.serial.id);
          } else {
            onSerialError?.(sr.reason ?? "Serial không hợp lệ");
          }
          return;
        }
        // kind === "none" → still try variant search below (may be SKU/barcode)
        if (sr.kind === "none") {
          // fall through to variant search
        }
        // kind === "variant" → already have matches from scan, use them
        if (sr.kind === "variant") {
          setValue("");
          if (sr.matches.length === 0) return;
          if (sr.matches.length === 1) {
            onAdd(sr.matches[0].id);
          } else {
            // Remap to VariantResult shape expected by onMultiple
            onMultiple(
              sr.matches.map((m) => ({
                id: m.id,
                sku: m.sku,
                productId: "",
                productName: m.productName,
                sellPrice: m.sellPrice,
                costPrice: "0",
                barcode: m.barcode ?? null,
              })),
            );
          }
          return;
        }
      }

      // Variant-search fallback (non-serial items or no resolver).
      const results = await apiGet<VariantResult[]>("/v1/variants/search", { q, limit: 5 });
      setValue("");
      if (!results || results.length === 0) return;
      if (results.length === 1) {
        onAdd(results[0].id, results[0].imageUrls?.[0]);
      } else {
        onMultiple(results);
      }
    } catch {
      // Silently ignore — cashier can try again. Specific serial errors surfaced via onSerialError.
    } finally {
      setBusy(false);
      ref.current?.focus();
    }
  };

  return (
    <Input
      ref={ref}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder="Quét / nhập barcode hoặc SKU rồi Enter..."
      disabled={disabled || busy}
      className="font-mono"
      autoComplete="off"
      data-testid="barcode-input"
    />
  );
}
